import type { Registry as Services } from '@ember/service';
import type MarketPerformanceDataItem from 'lumen/models/market-performance/data-item';
import type { MarketPerformanceMeta } from 'lumen/models/market-performance/data-item';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import {
  GROUP_BY,
  type GroupBy,
  type ShowAs,
} from 'lumen/utils/constants/market-snapshots-constants';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

/** URL query params shared by every snapshot view (validated in the transition service). */
export type MarketSnapshotsParams = {
  fromDate?: string;
  groupBy?: GroupBy;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  showAs?: ShowAs;
  toDate?: string;
};

export interface SnapshotItemsServices {
  alerting: Services['alerting'];
  errorTracking: Services['error-tracking'];
  store: Services['store'];
}

export type SnapshotDateRange = { from: string; to: string };

export type SnapshotPagination = { page: number; pageSize: number };

/** Month rows are few and API-ordered: no sort, no pagination. */
export type SnapshotRows =
  | { groupBy: typeof GROUP_BY.MONTH }
  | {
      groupBy: typeof GROUP_BY.PROPERTY;
      orderBy: string;
      pagination: SnapshotPagination;
    };

export interface SnapshotItemsParams {
  compare: string;
  dateRange: SnapshotDateRange;
  marketId: string;
  rows: SnapshotRows;
  view: string;
}

export interface SnapshotItems {
  items: MarketPerformanceDataItem[];
  meta: MarketPerformanceMeta;
  // The params this result was loaded with, so consumers can tell whether it
  // still matches the current query params.
  params: SnapshotItemsParams;
}

/** Loads one page of a market snapshot view; errors alert, are tracked and rethrown silently. */
export async function fetchSnapshotItems(
  { alerting, errorTracking, store }: SnapshotItemsServices,
  params: SnapshotItemsParams,
): Promise<SnapshotItems> {
  const { compare, dateRange, marketId, rows, view } = params;

  try {
    const response = await store.query<MarketPerformanceDataItem>(
      'market-performance/data-item',
      {
        namespace: APIGATEWAY,
        typeKey: `reservations/market/${marketId}/performance`,
        view,
        compare,
        group_by: rows.groupBy,
        start_date: dateRange.from,
        end_date: dateRange.to,
        market_id: marketId,
        // Month rows are few and API-ordered: no sort, no pagination.
        ...(rows.groupBy === GROUP_BY.PROPERTY && {
          order_by: rows.orderBy,
          page: rows.pagination.page,
          page_size: rows.pagination.pageSize,
        }),
      },
    );

    return {
      items: response,
      meta: response.meta as unknown as MarketPerformanceMeta,
      params,
    };
  } catch (error) {
    void alerting.showErrorAlert();
    errorTracking.captureException(error);

    throw new SilentTaskError(getErrorMessage(error));
  }
}
